import { darkPalette, lightPalette, tokenToCssValue, tokens, type WidgetPalette } from './model';
import {
  SURFACE_MOUNT_V1_ATTRIBUTES,
  SURFACE_MOUNT_V1_CONTRACT_VERSION,
  SURFACE_MOUNT_V1_HOOKS,
} from '@find-my-part/types';
import { HUB_LAYOUT_V1_INDEX_LINK_SELECTOR, HUB_LAYOUT_V1_SHELL_SELECTOR } from './hubLayout';

export const WIDGET_TOKEN_SCOPE_CLASS = 'fmp-widget-scope';
export const WIDGET_TOKEN_SCOPE_SELECTOR = `.${WIDGET_TOKEN_SCOPE_CLASS}`;
export const WIDGET_SCHEME_ATTRIBUTE = 'data-fmp-scheme';
export const SURFACE_MOUNT_V1_HUB_RESERVATION_RELEASED_CLASS =
  'fmp-widget-scope--hub-reservation-released';

export interface ResolvedThemeValues {
  readonly accent: string;
  readonly accentHover: string;
  readonly accentSubtle: string;
  readonly onAccent: string;
  readonly surface: string;
  readonly surfaceSubtle: string;
  readonly surfaceHover: string;
  readonly text: string;
  readonly textSecondary: string;
  readonly textMuted: string;
  readonly border: string;
  readonly focus: string;
  readonly radius: string;
  readonly fontFamily: string;
}

export function defaultThemeValues(palette: WidgetPalette): ResolvedThemeValues {
  return Object.freeze({
    accent: tokenToCssValue(palette.accent),
    accentHover: tokenToCssValue(palette.accentHover),
    accentSubtle: tokenToCssValue(palette.accentSubtle),
    onAccent: tokenToCssValue(palette.onAccent),
    surface: tokenToCssValue(palette.surface),
    surfaceSubtle: tokenToCssValue(palette.surfaceSubtle),
    surfaceHover: tokenToCssValue(palette.surfaceHover),
    text: tokenToCssValue(palette.text),
    textSecondary: tokenToCssValue(palette.textSecondary),
    textMuted: tokenToCssValue(palette.textMuted),
    border: tokenToCssValue(palette.border),
    focus: tokenToCssValue(palette.focus),
    radius: tokenToCssValue(tokens.dimension.radius.control),
    fontFamily: tokenToCssValue(tokens.typography.family.inherited),
  });
}

/**
 * The private implementation seam. Public `--fmp-theme-*` input is never
 * referenced here: validated, normalized values are passed in as `theme`.
 */
export function buildInternalTokenProperties(
  palette: WidgetPalette,
  theme: ResolvedThemeValues = defaultThemeValues(palette)
): Readonly<Record<`--fmp-${string}`, string>> {
  const resting = tokenToCssValue(tokens.elevation.resting);
  const raised = tokenToCssValue(tokens.elevation.raised);
  const ease = tokenToCssValue(tokens.motion.easing.standard);

  return Object.freeze({
    '--fmp-color-page': tokenToCssValue(palette.page),
    '--fmp-color-surface': theme.surface,
    '--fmp-color-surface-muted': theme.surfaceSubtle,
    '--fmp-color-progress-track': theme.surfaceSubtle,
    '--fmp-color-surface-hover': theme.surfaceHover,
    '--fmp-color-text': theme.text,
    '--fmp-color-text-secondary': theme.textSecondary,
    '--fmp-color-text-muted': theme.textMuted,
    '--fmp-color-text-hint': theme.textMuted,
    '--fmp-color-border': theme.border,
    '--fmp-color-border-light': theme.border,
    '--fmp-color-separator': theme.border,
    '--fmp-color-primary': theme.accent,
    '--fmp-color-primary-hover': theme.accentHover,
    '--fmp-color-primary-light': theme.accentSubtle,
    '--fmp-color-text-on-primary': theme.onAccent,
    '--fmp-color-focus': theme.focus,

    '--fmp-color-success': tokenToCssValue(palette.status.success.text),
    '--fmp-color-success-bg': tokenToCssValue(palette.status.success.surface),
    '--fmp-color-success-border': tokenToCssValue(palette.status.success.border),
    '--fmp-color-warning-text': tokenToCssValue(palette.status.warning.text),
    '--fmp-color-warning-bg': tokenToCssValue(palette.status.warning.surface),
    '--fmp-color-warning-border': tokenToCssValue(palette.status.warning.border),
    '--fmp-color-danger': tokenToCssValue(palette.status.danger.text),
    '--fmp-color-danger-bg': tokenToCssValue(palette.status.danger.surface),
    '--fmp-color-danger-border': tokenToCssValue(palette.status.danger.border),
    '--fmp-color-error': tokenToCssValue(palette.status.danger.text),
    '--fmp-color-error-dark': tokenToCssValue(palette.status.danger.text),
    '--fmp-color-error-bg': tokenToCssValue(palette.status.danger.surface),
    '--fmp-color-error-border': tokenToCssValue(palette.status.danger.border),
    '--fmp-color-info': tokenToCssValue(palette.status.info.text),
    '--fmp-color-info-text': tokenToCssValue(palette.status.info.text),
    '--fmp-color-info-bg': tokenToCssValue(palette.status.info.surface),
    '--fmp-color-info-border': tokenToCssValue(palette.status.info.border),
    '--fmp-color-skeleton': tokenToCssValue(palette.skeleton.base),
    '--fmp-color-skeleton-highlight': tokenToCssValue(palette.skeleton.highlight),
    '--fmp-color-overlay': tokenToCssValue(palette.overlay),
    '--fmp-color-overlay-text': tokenToCssValue(palette.overlayText),
    '--fmp-color-overlay-control': tokenToCssValue(palette.overlayControl),
    '--fmp-color-overlay-control-hover': tokenToCssValue(palette.overlayControlHover),
    '--fmp-color-overlay-caption': tokenToCssValue(palette.overlayCaption),

    '--fmp-font-family': theme.fontFamily,
    '--fmp-font-size-xs': tokenToCssValue(tokens.typography.size.xs),
    '--fmp-font-size-sm': tokenToCssValue(tokens.typography.size.sm),
    '--fmp-font-size-base': tokenToCssValue(tokens.typography.size.base),
    '--fmp-font-size-lg': tokenToCssValue(tokens.typography.size.lg),
    '--fmp-font-size-xl': tokenToCssValue(tokens.typography.size.xl),
    '--fmp-font-size-2xl': tokenToCssValue(tokens.typography.size.xxl),
    '--fmp-font-weight-regular': tokenToCssValue(tokens.typography.weight.regular),
    '--fmp-font-weight-semibold': tokenToCssValue(tokens.typography.weight.semibold),
    '--fmp-line-height-body': tokenToCssValue(tokens.typography.lineHeight.body),
    '--fmp-line-height-heading': tokenToCssValue(tokens.typography.lineHeight.heading),
    '--fmp-line-height-icon': tokenToCssValue(tokens.typography.lineHeight.icon),
    '--fmp-line-height-tight': tokenToCssValue(tokens.typography.lineHeight.tight),
    '--fmp-letter-spacing-label': tokenToCssValue(tokens.typography.letterSpacing.label),

    '--fmp-space-xs': tokenToCssValue(tokens.dimension.space.xs),
    '--fmp-space-sm': tokenToCssValue(tokens.dimension.space.sm),
    '--fmp-space-md': tokenToCssValue(tokens.dimension.space.md),
    '--fmp-space-lg': tokenToCssValue(tokens.dimension.space.lg),
    '--fmp-space-xl': tokenToCssValue(tokens.dimension.space.xl),
    '--fmp-space-2xl': tokenToCssValue(tokens.dimension.space.xxl),
    '--fmp-space-section': tokenToCssValue(tokens.dimension.space.xxxl),
    '--fmp-space-4xl': tokenToCssValue(tokens.dimension.space.xxxxl),

    '--fmp-radius-sm': tokenToCssValue(tokens.dimension.radius.compact),
    '--fmp-radius-md': theme.radius,
    '--fmp-radius-lg': tokenToCssValue(tokens.dimension.radius.surface),
    '--fmp-radius-pill': tokenToCssValue(tokens.dimension.radius.pill),
    '--fmp-radius-control': theme.radius,
    '--fmp-radius-surface': tokenToCssValue(tokens.dimension.radius.surface),

    '--fmp-shadow-sm': resting,
    '--fmp-shadow-md': raised,
    '--fmp-shadow-lg': raised,
    '--fmp-shadow-resting': resting,
    '--fmp-shadow-raised': raised,
    '--fmp-shadow-focus': `0 0 0 ${tokenToCssValue(tokens.dimension.geometry.three)} ${theme.accentSubtle}`,
    '--fmp-shadow-focus-danger': `0 0 0 ${tokenToCssValue(tokens.dimension.geometry.three)} ${tokenToCssValue(palette.status.danger.surface)}`,

    '--fmp-z-base': tokenToCssValue(tokens.layer.base),
    '--fmp-z-raised': tokenToCssValue(tokens.layer.raised),
    '--fmp-z-skip-link': tokenToCssValue(tokens.layer.skipLink),
    '--fmp-z-modal': tokenToCssValue(tokens.layer.modal),
    '--fmp-outline-width': tokenToCssValue(tokens.outline.width),
    '--fmp-outline-offset': tokenToCssValue(tokens.outline.offset),
    '--fmp-outline-inset-offset': tokenToCssValue(tokens.outline.insetOffset),
    '--fmp-outline-style': tokenToCssValue(tokens.outline.style),
    '--fmp-duration-instant': tokenToCssValue(tokens.motion.duration.instant),
    '--fmp-duration-fast': tokenToCssValue(tokens.motion.duration.fast),
    '--fmp-duration-normal': tokenToCssValue(tokens.motion.duration.normal),
    '--fmp-duration-slow': tokenToCssValue(tokens.motion.duration.slow),
    '--fmp-duration-spin': tokenToCssValue(tokens.motion.duration.spin),
    '--fmp-duration-shimmer': tokenToCssValue(tokens.motion.duration.shimmer),
    '--fmp-ease-standard': ease,

    '--fmp-border-width': tokenToCssValue(tokens.dimension.border.hairline),
    '--fmp-border-width-strong': tokenToCssValue(tokens.dimension.border.focus),
    '--fmp-focus-width': tokenToCssValue(tokens.dimension.border.focus),
    '--fmp-size-1': tokenToCssValue(tokens.dimension.geometry.nudge),
    '--fmp-size-2': tokenToCssValue(tokens.dimension.geometry.xxs),
    '--fmp-size-3': tokenToCssValue(tokens.dimension.geometry.three),
    '--fmp-size-4': tokenToCssValue(tokens.dimension.space.xs),
    '--fmp-size-6': tokenToCssValue(tokens.dimension.geometry.six),
    '--fmp-size-8': tokenToCssValue(tokens.dimension.geometry.eight),
    '--fmp-size-10': tokenToCssValue(tokens.dimension.geometry.ten),
    '--fmp-size-12': tokenToCssValue(tokens.dimension.geometry.twelve),
    '--fmp-size-14': tokenToCssValue(tokens.dimension.geometry.fourteen),
    '--fmp-size-15': tokenToCssValue(tokens.dimension.geometry.fifteen),
    '--fmp-size-16': tokenToCssValue(tokens.dimension.geometry.sixteen),
    '--fmp-size-18': tokenToCssValue(tokens.dimension.geometry.eighteen),
    '--fmp-size-20': tokenToCssValue(tokens.dimension.geometry.twenty),
    '--fmp-size-24': tokenToCssValue(tokens.dimension.geometry.twentyFour),
    '--fmp-size-25': tokenToCssValue(tokens.dimension.geometry.twentyFive),
    '--fmp-size-28': tokenToCssValue(tokens.dimension.geometry.twentyEight),
    '--fmp-size-31': tokenToCssValue(tokens.dimension.geometry.thirtyOne),
    '--fmp-size-32': tokenToCssValue(tokens.dimension.geometry.thirtyTwo),
    '--fmp-size-36': tokenToCssValue(tokens.dimension.geometry.thirtySix),
    '--fmp-size-40': tokenToCssValue(tokens.dimension.geometry.forty),
    '--fmp-size-44': tokenToCssValue(tokens.dimension.geometry.touch),
    '--fmp-size-48': tokenToCssValue(tokens.dimension.geometry.fortyEight),
    '--fmp-size-50': tokenToCssValue(tokens.dimension.geometry.fifty),
    '--fmp-size-60': tokenToCssValue(tokens.dimension.geometry.sixty),
    '--fmp-size-64': tokenToCssValue(tokens.dimension.geometry.sixtyFour),
    '--fmp-size-65': tokenToCssValue(tokens.dimension.geometry.sixtyFive),
    '--fmp-size-72': tokenToCssValue(tokens.dimension.geometry.seventyTwo),
    '--fmp-size-80': tokenToCssValue(tokens.dimension.geometry.eighty),
    '--fmp-size-96': tokenToCssValue(tokens.dimension.geometry.ninetySix),
    '--fmp-size-100': tokenToCssValue(tokens.dimension.geometry.oneHundred),
    '--fmp-size-110': tokenToCssValue(tokens.dimension.geometry.oneTen),
    '--fmp-size-120': tokenToCssValue(tokens.dimension.geometry.oneTwenty),
    '--fmp-size-160': tokenToCssValue(tokens.dimension.geometry.oneSixty),
    '--fmp-size-200': tokenToCssValue(tokens.dimension.geometry.twoHundred),
    '--fmp-size-300': tokenToCssValue(tokens.dimension.geometry.threeHundred),
    '--fmp-size-320': tokenToCssValue(tokens.dimension.geometry.threeTwenty),
    '--fmp-size-400': tokenToCssValue(tokens.dimension.geometry.fourHundred),
    '--fmp-size-480': tokenToCssValue(tokens.dimension.geometry.fourEighty),
    '--fmp-size-600': tokenToCssValue(tokens.dimension.geometry.sixHundred),
    '--fmp-layout-widget-max': tokenToCssValue(tokens.layout.widgetMax),
    '--fmp-layout-view-max': tokenToCssValue(tokens.layout.viewMax),
    '--fmp-layout-prose-max': tokenToCssValue(tokens.layout.proseMax),
    '--fmp-layout-lightbox-max-inline': tokenToCssValue(tokens.layout.lightboxMaxInline),
    '--fmp-layout-lightbox-max-block': tokenToCssValue(tokens.layout.lightboxMaxBlock),
    '--fmp-skeleton-title-inline': tokenToCssValue(tokens.layout.skeleton.titleInline),
    '--fmp-skeleton-title-block': tokenToCssValue(tokens.layout.skeleton.titleBlock),
    '--fmp-skeleton-description-inline': tokenToCssValue(tokens.layout.skeleton.descriptionInline),
    '--fmp-skeleton-description-block': tokenToCssValue(tokens.layout.skeleton.descriptionBlock),
    '--fmp-skeleton-badge-inline': tokenToCssValue(tokens.layout.skeleton.badgeInline),
    '--fmp-skeleton-badge-block': tokenToCssValue(tokens.layout.skeleton.badgeBlock),
    '--fmp-skeleton-meta-inline': tokenToCssValue(tokens.layout.skeleton.metaInline),
    '--fmp-skeleton-meta-short-inline': tokenToCssValue(tokens.layout.skeleton.metaShortInline),
    '--fmp-skeleton-meta-block': tokenToCssValue(tokens.layout.skeleton.metaBlock),
  });
}

export const INTERNAL_TOKEN_PROPERTIES = Object.freeze(
  Object.keys(buildInternalTokenProperties(lightPalette)) as Array<`--fmp-${string}`>
);

function declarations(properties: Readonly<Record<string, string>>): string {
  return Object.entries(properties)
    .map(([property, value]) => `    ${property}: ${value};`)
    .join('\n');
}

export const SKELETON_CSS = `
${WIDGET_TOKEN_SCOPE_SELECTOR} :where(.fmp-skeleton__title, .fmp-skeleton__description, .fmp-skeleton__badge, .fmp-skeleton__meta, .fmp-skeleton) {
  background-color: var(--fmp-color-skeleton);
  background-image: linear-gradient(90deg, var(--fmp-color-skeleton), var(--fmp-color-skeleton-highlight), var(--fmp-color-skeleton));
  background-size: 200% 100%;
  animation: fmp-shimmer var(--fmp-duration-shimmer) var(--fmp-ease-standard) infinite;
  border-radius: var(--fmp-radius-sm);
}

${WIDGET_TOKEN_SCOPE_SELECTOR} .fmp-skeleton__title {
  width: var(--fmp-skeleton-title-inline);
  height: var(--fmp-skeleton-title-block);
  flex: none;
}

${WIDGET_TOKEN_SCOPE_SELECTOR} .fmp-skeleton__description {
  width: var(--fmp-skeleton-description-inline);
  height: var(--fmp-skeleton-description-block);
}

${WIDGET_TOKEN_SCOPE_SELECTOR} .fmp-skeleton__badge {
  width: var(--fmp-skeleton-badge-inline);
  height: var(--fmp-skeleton-badge-block);
  border-radius: var(--fmp-radius-pill);
  flex-shrink: 0;
}

${WIDGET_TOKEN_SCOPE_SELECTOR} .fmp-skeleton__meta {
  width: var(--fmp-skeleton-meta-inline);
  height: var(--fmp-skeleton-meta-block);
}

${WIDGET_TOKEN_SCOPE_SELECTOR} .fmp-skeleton__meta--short {
  width: var(--fmp-skeleton-meta-short-inline);
}

${WIDGET_TOKEN_SCOPE_SELECTOR} .fmp-visually-hidden {
  position: absolute;
  width: var(--fmp-size-1);
  height: var(--fmp-size-1);
  padding: 0;
  margin: calc(-1 * var(--fmp-size-1));
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

@keyframes fmp-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}`;

/** Emit both palettes and the widget-local normalization from the canonical model. */
export function emitTokenCss(): string {
  const light = declarations(buildInternalTokenProperties(lightPalette));
  const dark = declarations(buildInternalTokenProperties(darkPalette));
  return `
${HUB_LAYOUT_V1_SHELL_SELECTOR} {
  box-sizing: border-box;
  inline-size: 100%;
  max-inline-size: ${tokenToCssValue(tokens.layout.viewMax)};
  margin-inline: auto;
}

${HUB_LAYOUT_V1_INDEX_LINK_SELECTOR} {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-inline-size: ${tokenToCssValue(tokens.dimension.geometry.touch)};
  min-block-size: ${tokenToCssValue(tokens.dimension.geometry.touch)};
}

${WIDGET_TOKEN_SCOPE_SELECTOR} {
${light}
  color-scheme: light;
  color: var(--fmp-color-text);
  font-family: var(--fmp-font-family);
}

${WIDGET_TOKEN_SCOPE_SELECTOR}[${SURFACE_MOUNT_V1_ATTRIBUTES.contractVersion}~="${SURFACE_MOUNT_V1_CONTRACT_VERSION}"][${SURFACE_MOUNT_V1_ATTRIBUTES.surface}~="hub"]:not(.${SURFACE_MOUNT_V1_HUB_RESERVATION_RELEASED_CLASS}) {
  display: flow-root;
  min-block-size: var(--fmp-size-200);
}

${WIDGET_TOKEN_SCOPE_SELECTOR}[${SURFACE_MOUNT_V1_ATTRIBUTES.contractVersion}~="${SURFACE_MOUNT_V1_CONTRACT_VERSION}"][${SURFACE_MOUNT_V1_ATTRIBUTES.surface}~="question-actions"] > [${SURFACE_MOUNT_V1_HOOKS.questionActionsSlot.attributeName}="${SURFACE_MOUNT_V1_HOOKS.questionActionsSlot.value}"] > :first-child {
  min-block-size: var(--fmp-size-64);
}

${WIDGET_TOKEN_SCOPE_SELECTOR}[${WIDGET_SCHEME_ATTRIBUTE}="dark"] {
${dark}
  color-scheme: dark;
}

:where(${WIDGET_TOKEN_SCOPE_SELECTOR}, ${WIDGET_TOKEN_SCOPE_SELECTOR} *, ${WIDGET_TOKEN_SCOPE_SELECTOR} *::before, ${WIDGET_TOKEN_SCOPE_SELECTOR} *::after) {
  box-sizing: border-box;
}

:where(${WIDGET_TOKEN_SCOPE_SELECTOR} button, ${WIDGET_TOKEN_SCOPE_SELECTOR} input, ${WIDGET_TOKEN_SCOPE_SELECTOR} select, ${WIDGET_TOKEN_SCOPE_SELECTOR} textarea) {
  color: var(--fmp-color-text);
  font: inherit;
}

${SKELETON_CSS}

@media (prefers-reduced-motion: reduce) {
  :where(${WIDGET_TOKEN_SCOPE_SELECTOR}, ${WIDGET_TOKEN_SCOPE_SELECTOR} *, ${WIDGET_TOKEN_SCOPE_SELECTOR} *::before, ${WIDGET_TOKEN_SCOPE_SELECTOR} *::after) {
    animation-delay: var(--fmp-duration-instant) !important;
    animation-duration: var(--fmp-duration-instant) !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-delay: var(--fmp-duration-instant) !important;
    transition-duration: var(--fmp-duration-instant) !important;
  }
}

@media (forced-colors: active) {
  ${WIDGET_TOKEN_SCOPE_SELECTOR} {
    --fmp-color-page: Canvas !important;
    --fmp-color-surface: Canvas !important;
    --fmp-color-surface-muted: Canvas !important;
    --fmp-color-progress-track: Canvas !important;
    --fmp-color-surface-hover: Highlight !important;
    --fmp-color-text: CanvasText !important;
    --fmp-color-text-secondary: CanvasText !important;
    --fmp-color-text-muted: CanvasText !important;
    --fmp-color-text-hint: CanvasText !important;
    --fmp-color-border: CanvasText !important;
    --fmp-color-border-light: CanvasText !important;
    --fmp-color-separator: CanvasText !important;
    --fmp-color-primary: LinkText !important;
    --fmp-color-primary-hover: LinkText !important;
    --fmp-color-primary-light: Canvas !important;
    --fmp-color-text-on-primary: ButtonText !important;
    --fmp-color-focus: Highlight !important;
    --fmp-color-success: CanvasText !important;
    --fmp-color-success-bg: Canvas !important;
    --fmp-color-success-border: CanvasText !important;
    --fmp-color-warning-text: CanvasText !important;
    --fmp-color-warning-bg: Canvas !important;
    --fmp-color-warning-border: CanvasText !important;
    --fmp-color-danger: CanvasText !important;
    --fmp-color-danger-bg: Canvas !important;
    --fmp-color-danger-border: CanvasText !important;
    --fmp-color-error: CanvasText !important;
    --fmp-color-error-dark: CanvasText !important;
    --fmp-color-error-bg: Canvas !important;
    --fmp-color-error-border: CanvasText !important;
    --fmp-color-info: CanvasText !important;
    --fmp-color-info-text: CanvasText !important;
    --fmp-color-info-bg: Canvas !important;
    --fmp-color-info-border: CanvasText !important;
    --fmp-color-skeleton: Canvas !important;
    --fmp-color-skeleton-highlight: CanvasText !important;
    --fmp-color-overlay: Canvas !important;
    --fmp-color-overlay-text: CanvasText !important;
    --fmp-color-overlay-control: ButtonFace !important;
    --fmp-color-overlay-control-hover: Highlight !important;
    --fmp-color-overlay-caption: Canvas !important;
    forced-color-adjust: auto;
  }

  ${WIDGET_TOKEN_SCOPE_SELECTOR} :where(.fmp-btn--primary, .fmp-error__button, .fmp-skip-link, .fmp-preview__remove, .fmp-pagination__page--active, .fmp-upload-action--camera, .fmp-nav__ask) {
    background-color: ButtonFace !important;
    border: var(--fmp-border-width-strong) solid ButtonText !important;
    color: ButtonText !important;
  }

  ${WIDGET_TOKEN_SCOPE_SELECTOR} :where(button:disabled, [aria-disabled='true']) {
    border-color: GrayText !important;
    color: GrayText !important;
    opacity: 1;
  }

  ${WIDGET_TOKEN_SCOPE_SELECTOR} :where(a, button, input, select, textarea, [tabindex]):focus-visible {
    border-color: Highlight !important;
    box-shadow: none !important;
    outline: var(--fmp-outline-width) var(--fmp-outline-style) Highlight !important;
    outline-offset: var(--fmp-outline-offset) !important;
  }
}`;
}

export const WIDGET_TOKEN_CSS = emitTokenCss();
